/*
 * This is a reimplementation of what exists in our HTML template files
 * already. Once the React tree mounts, this is what gets rendered first, until
 * the app is ready to go.
 */

import {useEffect, useRef, useState} from 'react'
import Svg, {Path} from 'react-native-svg'

import {atoms as a, flatten} from '#/alf'

const size = 100
const ratio = 100 / 102

export function Splash({
  isReady,
  children,
}: React.PropsWithChildren<{
  isReady: boolean
}>) {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false)
  const splashRef = useRef<HTMLDivElement>(null)

  // hide the static one that's baked into the HTML - gets replaced by our React version below
  useEffect(() => {
    // double rAF ensures that the React version gets painted first
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const splash = document.getElementById('splash')
        if (splash) {
          splash.remove()
        }
      })
    })
  }, [])

  // when ready, we fade/scale out
  useEffect(() => {
    if (!isReady) return

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches
    const node = splashRef.current
    if (!node || reduceMotion) {
      setIsAnimationComplete(true)
      return
    }

    const animation = node.animate(
      [
        {opacity: 1, transform: 'scale(1)'},
        {opacity: 0, transform: 'scale(1.5)'},
      ],
      {
        duration: 300,
        easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        fill: 'forwards',
      },
    )
    animation.onfinish = () => setIsAnimationComplete(true)

    return () => {
      animation.cancel()
    }
  }, [isReady])

  return (
    <>
      {isReady && children}

      {!isAnimationComplete && (
        <div
          ref={splashRef}
          style={flatten([
            a.fixed,
            a.inset_0,
            a.flex,
            a.align_center,
            a.justify_center,
            // to compensate for the `top: -50px` below
            {transformOrigin: 'center calc(50% - 50px)'},
          ])}>
          <Svg
            fill="none"
            viewBox="0 0 102 100"
            style={[a.relative, {width: size, height: size * ratio, top: -50}]}>
            <Path
              fill="#006AFF"
              d="M29.812 20.4709C34.2089 20.4709 37.7734 16.9065 37.7734 12.5095C37.7734 8.11258 34.2089 4.54816 29.812 4.54816C25.4151 4.54816 21.8506 8.11258 21.8506 12.5095C21.8506 16.9065 25.4151 20.4709 29.812 20.4709Z M72.2606 20.5162C76.6575 20.5162 80.2219 16.9517 80.2219 12.5548C80.2219 8.15787 76.6575 4.59344 72.2606 4.59344C67.8636 4.59344 64.2992 8.15787 64.2992 12.5548C64.2992 16.9517 67.8636 20.5162 72.2606 20.5162Z M94.0387 58.0808C98.4356 58.0808 102 54.5164 102 50.1194C102 45.7225 98.4356 42.1581 94.0387 42.1581C89.6418 42.1581 86.0773 45.7225 86.0773 50.1194C86.0773 54.5164 89.6418 58.0808 94.0387 58.0808Z M72.9324 94.9826C77.3293 94.9826 80.8937 91.4182 80.8937 87.0212C80.8937 82.6243 77.3293 79.0599 72.9324 79.0599C68.5354 79.0599 64.971 82.6243 64.971 87.0212C64.971 91.4182 68.5354 94.9826 72.9324 94.9826Z M29.2308 95.0824C33.6277 95.0824 37.1921 91.518 37.1921 87.121C37.1921 82.7241 33.6277 79.1597 29.2308 79.1597C24.8338 79.1597 21.2694 82.7241 21.2694 87.121C21.2694 91.518 24.8338 95.0824 29.2308 95.0824Z M7.96136 57.1548C12.3583 57.1548 15.9227 53.5904 15.9227 49.1934C15.9227 44.7965 12.3583 41.2321 7.96136 41.2321C3.56442 41.2321 0 44.7965 0 49.1934C0 53.5904 3.56442 57.1548 7.96136 57.1548Z M50.1012 0V37.9549C46.5517 38.2363 43.4561 40.1427 41.5588 42.9205L8.76019 24.0566C12.0464 19.2906 18.4918 17.7746 23.6026 20.7159L39.8068 30.0389V11.2022C39.8068 5.3106 44.3367 0.481131 50.1012 0Z M93.8116 23.9114L60.9041 42.8298C58.8888 39.8885 55.7024 38.1637 52.3436 37.9005L52.3617 0.0635986C58.1262 0.535652 62.6561 5.36512 62.6561 11.2658V29.9573L78.9783 20.5707C84.0891 17.6385 90.5254 19.1545 93.8207 23.9204L93.8116 23.9114Z M94.7919 73.8855L62.0568 54.6675C63.6092 51.463 63.5365 47.8318 62.1022 44.7816L94.9735 26.0538C97.4154 31.2918 95.4637 37.6191 90.3437 40.5422L74.1033 49.7927L90.3437 59.3336C95.4274 62.3202 97.2974 68.6657 94.7828 73.8855H94.7919Z M52.1437 99.4672L52.416 61.5123C55.9655 61.2581 59.0701 59.3789 60.9947 56.6102L93.648 75.7102C90.3346 80.4489 83.8711 81.9195 78.7874 78.951L62.6468 69.519L62.5107 88.3557C62.4653 94.2473 57.9082 99.0405 52.1346 99.4762L52.1437 99.4672Z M8.79639 75.583L41.6949 56.6464C43.7102 59.5785 46.9056 61.3124 50.2644 61.5757V99.4126C44.509 98.9405 39.9791 94.111 39.9791 88.2195V69.528L23.6479 78.9236C18.5461 81.8649 12.1008 80.3489 8.80546 75.592L8.79639 75.583Z M7.77982 25.6361L40.5331 44.8178C38.9807 48.0223 39.0624 51.6535 40.4967 54.7037L7.64365 73.4678C5.19261 68.2298 7.14436 61.9025 12.2552 58.9703L28.4866 49.7017L12.2371 40.179C7.15344 37.2014 5.27431 30.8559 7.77982 25.6361Z"
            />
          </Svg>
        </div>
      )}
    </>
  )
}
